/* eslint-disable max-lines */
import { DatePickerEvent } from '@dnb/eufemia/components/DatePicker';
import ErrorContext from '@portals/shared-frontend/hooks/ErrorContext';
import {
  type Controller,
  type FormValues,
  type Schema,
  type SchemaOutput,
  type SchemaPath,
  useForm,
} from '@portals/shared-frontend/hooks/useForm';
import { debounce, deepGet } from '@portals/shared-frontend/utils';
import { ApiError } from '@portals/shared-frontend/utils/ApiError';
import { ChangeEvent, type FormEvent, useCallback, useContext, useRef, useState } from 'react';

import { useMemoizedThunk, useMemoizedThunkWeak } from './useThunk';

/**
 * One giant hack to make Eufemia form elements behave more consistently
 *
 * TODO: Should be refactored
 */

interface HandleSubmitCallback<T extends Schema> {
  (data: SchemaOutput<T>): void;
}

interface HandleSubmit<T extends Schema> {
  (callback: HandleSubmitCallback<T>): (event: FormEvent) => void;
}

type StatusProps = {
  status?: string | null;
};

type ValueProps<Event, Value = string> = StatusProps & {
  onChange(event: Event): void;
  on_change?(event: Event): void;
  value?: Value;
};

type CheckedProps<Event> = StatusProps & {
  checked?: boolean;
  onChange(event: Event): void;
  on_change?(event: Event): void;
};

type InputProps = ValueProps<{ value: string }>;

type TextareaProps = ValueProps<{ value: string }>;

type DropdownProps = ValueProps<{ value: unknown }, string>;

type AutocompleteProps = ValueProps<{ value?: unknown }, number | string>;

type CheckboxProps = CheckedProps<{ checked: boolean }>;

type RadioProps = CheckboxProps;

type RadioGroupProps = ValueProps<{ value: unknown }, string>;

type DatePickerProps = StatusProps & {
  date?: string;
  onChange(event: DatePickerEvent<ChangeEvent<HTMLInputElement>>): void;
  on_change?(event: DatePickerEvent<ChangeEvent<HTMLInputElement>>): void;
};

type ToggleButtonProps = CheckboxProps;

type ToggleButtonGroupProps = StatusProps & {
  value?: string;
  values?: string[];
  multiselect?: boolean;
  onChange(event: { value?: unknown; values?: unknown }): void;
  on_change?(event: { value?: unknown; values?: unknown }): void;
};

type SliderProps = StatusProps & {
  value?: number;
  onChange(event: { value?: unknown; values?: unknown }): void;
  on_change?(event: { value?: unknown; values?: unknown }): void;
};

interface PropFn<T extends Schema, U, O = never> {
  <Path extends string>(path: SchemaPath<T, Path>, options?: O): U;
}

interface EufemiaProps<T extends Schema> {
  input: PropFn<T, InputProps>;
  textarea: PropFn<T, TextareaProps>;
  checkbox: PropFn<T, CheckboxProps>;
  radio: PropFn<T, RadioProps>;
  radioGroup: PropFn<T, RadioGroupProps>;
  dropdown: PropFn<T, DropdownProps>;
  autocomplete: PropFn<T, AutocompleteProps>;
  slider: PropFn<T, SliderProps>;
  datePicker: PropFn<T, DatePickerProps>;
  toggleButton: PropFn<T, ToggleButtonProps>;
  toggleButtonGroup: PropFn<T, ToggleButtonGroupProps, { multiselect?: boolean }>;
}

interface ChangeHandlerMap {
  input: InputProps['onChange'];
  textarea: TextareaProps['onChange'];
  checkbox: CheckboxProps['onChange'];
  radio: RadioProps['onChange'];
  radioGroup: RadioGroupProps['onChange'];
  dropdown: DropdownProps['onChange'];
  autocomplete: AutocompleteProps['onChange'];
  slider: SliderProps['onChange'];
  datePicker: DatePickerProps['onChange'];
  toggleButton: ToggleButtonProps['onChange'];
  toggleButtonGroup: ToggleButtonGroupProps['onChange'];
  toggleButtonGroupMultiselect: ToggleButtonGroupProps['onChange'];
}

function stringValue(value: unknown): string {
  if (value == null) {
    return '';
  }

  if (typeof value === 'object') {
    return value.toString();
  }

  if (typeof value === 'string') {
    return value;
  }

  return '';
}

export function useEufemiaProps<T extends Schema>(controller: Controller<T>): EufemiaProps<T> {
  const { values, errors, errorsRef, setValue: _setValue, getValue, triggerValidation } = controller;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedValidation = useCallback(debounce(triggerValidation, 50), [triggerValidation]);

  const setValue: typeof controller.setValue = useCallback(
    (path, value) => {
      if (value === getValue(path)) {
        return;
      }

      _setValue(path, value);

      if (errorsRef.current[path]) {
        debouncedValidation(path);
      }
    },
    [_setValue, debouncedValidation, errorsRef, getValue],
  );

  const getChangeHandler: PropFn<T, ChangeHandlerMap> = useMemoizedThunk(
    (path) => ({
      input({ value }) {
        setValue(path, value);
      },
      textarea({ value }) {
        setValue(path, value);
      },
      checkbox({ checked }) {
        setValue(path, checked);
      },
      radio({ checked }) {
        setValue(path, checked);
      },
      radioGroup({ value }) {
        setValue(path, value);
      },
      dropdown({ value }) {
        setValue(path, value);
      },
      autocomplete({ value }) {
        setValue(path, value);
      },
      slider({ value }) {
        setValue(path, value);
      },
      datePicker({ date }) {
        setValue(path, date ?? '');
      },
      toggleButton({ checked }) {
        setValue(path, checked);
      },
      toggleButtonGroup({ value }) {
        setValue(path, value);
      },
      toggleButtonGroupMultiselect({ values }) {
        setValue(path, values);
      },
    }),
    [setValue],
  );

  const extendBaseProps = <T,>(path: string, props: T): T => {
    const copy: T = {
      status: errors[path],
      ...props,
    };

    return copy;
  };

  const factories: EufemiaProps<T> = {
    input: (path) => {
      const value = deepGet(values, path);
      const changeHandler = getChangeHandler(path).input;
      return extendBaseProps(path, {
        value: stringValue(value),
        onChange: changeHandler,
        on_change: changeHandler,
      });
    },
    textarea: (path) => {
      const value = deepGet(values, path);
      const changeHandler = getChangeHandler(path).textarea;
      return extendBaseProps(path, {
        value: stringValue(value),
        onChange: changeHandler,
        on_change: changeHandler,
      });
    },
    checkbox: (path) => {
      const changeHandler = getChangeHandler(path).checkbox;
      return extendBaseProps(path, {
        checked: !!deepGet(values, path),
        onChange: changeHandler,
        on_change: changeHandler,
      });
    },
    radio: (path) => {
      const changeHandler = getChangeHandler(path).radio;
      return {
        checked: !!deepGet(values, path),
        onChange: changeHandler,
        on_change: changeHandler,
      };
    },
    radioGroup: (path) => {
      const value = deepGet(values, path);
      const changeHandler = getChangeHandler(path).radioGroup;
      return extendBaseProps(path, {
        value: stringValue(value),
        onChange: changeHandler,
        on_change: changeHandler,
      });
    },
    dropdown: (path) => {
      const value = deepGet(values, path);
      const changeHandler = getChangeHandler(path).dropdown;
      return extendBaseProps(path, {
        value: stringValue(value),
        onChange: changeHandler,
        on_change: changeHandler,
      });
    },
    autocomplete(path) {
      const value = deepGet(values, path);
      const changeHandler = getChangeHandler(path).autocomplete;
      return extendBaseProps(path, {
        value: typeof value === 'number' || typeof value === 'string' ? value : undefined,
        onChange: changeHandler,
        on_change: changeHandler,
      });
    },
    slider: (path) => {
      const value = deepGet(values, path);
      const changeHandler = getChangeHandler(path).slider;
      return extendBaseProps(path, {
        value: typeof value === 'number' ? value : undefined,
        onChange: changeHandler,
        on_change: changeHandler,
      });
    },
    datePicker: (path) => {
      const value = deepGet(values, path);
      const changeHandler = getChangeHandler(path).datePicker;
      return extendBaseProps(path, {
        date: stringValue(value),
        onChange: changeHandler,
        on_change: changeHandler,
      });
    },
    toggleButton: (path) => {
      const changeHandler = getChangeHandler(path).toggleButton;
      return extendBaseProps(path, {
        checked: !!deepGet(values, path),
        onChange: changeHandler,
        on_change: changeHandler,
      });
    },
    toggleButtonGroup: (path, options = {}) => {
      const value = deepGet(values, path);
      const changeHandler = options.multiselect
        ? getChangeHandler(path).toggleButtonGroupMultiselect
        : getChangeHandler(path).toggleButtonGroup;

      return extendBaseProps(path, {
        value: options.multiselect ? undefined : stringValue(value),
        values: options.multiselect ? (Array.isArray(value) ? value : []) : undefined,
        multiselect: !!options.multiselect,
        onChange: changeHandler,
        on_change: changeHandler,
      });
    },
  };

  return factories;
}

interface ExtendedController<T extends Schema> {
  controller: Controller<T>;
  handleSubmit: HandleSubmit<T>;
  register: EufemiaProps<T>;
  submitting: boolean;
}

export function useEufemiaForm<T extends Schema>(schema: T, initialValues: FormValues<T>): ExtendedController<T> {
  const { setGlobalError } = useContext(ErrorContext);
  const controller = useForm(initialValues, schema);
  const register = useEufemiaProps(controller);
  const [submitting, setSubmitting] = useState(false);
  const submitCounter = useRef(0);

  const { validate, setErrors, setFormError } = controller;

  const handleBackendError = useCallback(
    (error: unknown) => {
      if (ApiError.isApiError(error)) {
        if (error.body.code === 'ValidationError') {
          const errors = Object.fromEntries(
            Object.entries(error.body.errors).map(([prop, errors]) => [prop, errors?.[0]]),
          );

          setErrors(errors);
        } else if (error.body.code === 'BadRequestError') {
          setFormError(error.body.message);
        } else {
          setGlobalError(error.status);
        }
      } else {
        setGlobalError('unknown');
      }
    },
    [setErrors, setGlobalError, setFormError],
  );

  const handleSubmit: HandleSubmit<T> = useMemoizedThunkWeak(
    (callback) => async (event) => {
      event.preventDefault();
      const data = validate();

      if (!data) {
        return;
      }

      try {
        submitCounter.current++;
        setSubmitting(submitCounter.current > 0);
        await callback(data);
      } catch (error) {
        handleBackendError(error);
      } finally {
        submitCounter.current--;
        setSubmitting(submitCounter.current > 0);
      }
    },
    [validate, handleBackendError, setSubmitting],
  );

  return {
    controller,
    handleSubmit,
    register,
    submitting,
  };
}
